import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight, ShieldCheck, Sparkles, Truck, Star } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import Reveal from "@/components/common/Reveal";
import ProductCard from "@/components/common/ProductCard";
import CategoryCard from "@/components/common/CategoryCard";
import QuickView from "@/components/common/QuickView";
import RatingStars from "@/components/common/RatingStars";
import { TrustedMarquee } from "@/components/layout/TrustedMarquee";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { faqLd } from "@/lib/jsonld";
import { SEO_CONFIG } from "@/lib/seoConfig";
import { cn } from "@/lib/utils";
import { OurJourney } from "@/components/sections/OurJourney";

// FAQ content — used verbatim for BOTH the visible accordion and the FAQPage
// JSON-LD (single source of truth so the two can never drift).
const FAQS = [
  {
    q: "What products does AutoCareX make?",
    a: "AutoCareX is an Indian car care brand offering professional-grade products for at-home detailing — car shampoo, premium liquid wax, interior cleaner, dashboard polish, wheel cleaner, tyre shine, windshield wash, waterless wash, rust remover spray, AC foam duct cleaner, and microfibre cloths.",
  },
  {
    q: "Where can I buy AutoCareX products?",
    a: "You can buy AutoCareX directly at autocarex.in or on Amazon India, with delivery across the country.",
  },
  {
    q: "Are AutoCareX products certified?",
    a: "Yes. AutoCareX is manufactured to ISO 9001:2015 quality standards and holds CPCB and UPPCB environmental certifications. Products are made in India by JT Max India Enterprises.",
  },
  {
    q: "Can beginners use AutoCareX, or do I need detailing experience?",
    a: "No experience needed — AutoCareX is built for DIY car care. Any owner can get professional-looking results at home without special tools or training.",
  },
  {
    q: "How is AutoCareX different from cheap local products or expensive detailing services?",
    a: "AutoCareX sits between the two: more consistent and effective than low-cost local products, and far more affordable than premium detailing studios — accessible performance you apply yourself.",
  },
  {
    q: "Are AutoCareX products safe for car paint and interior surfaces?",
    a: "Yes...",
  },
  {
    q: "Can I clean my car without water?",
    a: "Yes. AutoCareX Waterless Wash lets you safely clean a lightly soiled car without a hose — ideal for apartments, water restrictions, or quick touch-ups between washes.",
  },
  {
    q: "How often should I wax my car?",
    a: "For most cars, applying a liquid wax like AutoCareX Premium Liquid Wax every 6-8 weeks keeps the paint protected, water-repellent, and glossy.",
  },
  {
    q: "What size do AutoCareX products come in?",
    a: "250ML & 5L",
  },
  {
    q: "What is AutoCareX's delivery and return policy?",
    a: "3-7 Business Days depending on location",
  },
];

function FaqAccordion({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div
      className="mx-auto mt-12 max-w-3xl border-y border-[color:var(--acx-border)]"
      data-testid="home-faq"
    >
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={item.q} className="border-b border-[color:var(--acx-border)] last:border-b-0">
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              data-testid={`home-faq-trigger-${i}`}
              className="flex w-full items-center justify-between gap-4 py-5 text-left transition-colors hover:text-white"
            >
              <span className="font-display text-[16px] font-semibold text-white sm:text-[18px]">
                {item.q}
              </span>
              <ChevronDown
                size={18}
                className={cn(
                  "shrink-0 transition-transform duration-200",
                  isOpen ? "rotate-180 text-[#E11B22]" : "text-white/45"
                )}
              />
            </button>
            {/* Answer stays in the DOM at all times (CSS height collapse), so it is
                present in the prerendered HTML even while visually collapsed. */}
            <div
              className={cn(
                "grid transition-all duration-200 ease-out",
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              )}
            >
              <div className="overflow-hidden">
                <p
                  data-testid={`home-faq-answer-${i}`}
                  className="pb-5 pr-8 text-[15px] leading-relaxed text-white/65"
                >
                  {item.a}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Testimonials({ items }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (!items.length) return;
    const t = setInterval(() => setActive((a) => (a + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);
  if (!items.length) return null;

  return (
    <section className="relative acx-noise border-t border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
      <div className="acx-container px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
        <Reveal>
          <p className="text-[12px] uppercase tracking-[0.22em] text-white/50">Testimonials</p>
          <h2 className="mt-3 max-w-2xl font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl">
            What our clients say
          </h2>
          <p className="mt-4 max-w-xl text-[16px] leading-relaxed text-white/60">
            Real impressions from car enthusiasts, detailers and everyday drivers who trust AutoCareX.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-testimonials">
          {items.slice(0, 6).map((t, i) => (
            <Reveal key={t.id} delay={i * 0.05}>
              <div
                className={`h-full rounded-[18px] border bg-white/[0.04] p-6 backdrop-blur-sm transition-all duration-500 ${
                  i === active % 6 ? "border-white/20 bg-white/[0.07]" : "border-[color:var(--acx-border)]"
                }`}
              >
                <RatingStars value={t.rating} size={15} />
                <p className="mt-4 text-[15px] leading-relaxed text-white/85">“{t.content}”</p>
                <div className="mt-5 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="h-10 w-10 rounded-full object-cover" />
                  <div>
                    <p className="text-[14px] font-semibold text-white">{t.name}</p>
                    <p className="text-[12px] text-white/50">{t.role} · {t.city}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [quickView, setQuickView] = useState(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    api.getCategories().then(setCategories).catch(() => {});
    api.getProducts({ featured: true, limit: 10 }).then((d) => setFeatured(d.items)).catch(() => {});
    api.getTestimonials().then(setTestimonials).catch(() => {});
  }, []);

  const scrollSlider = (dir) => {
    sliderRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  return (
    <div>
      <Seo
        title={SEO_CONFIG["/"].title}
        description={SEO_CONFIG["/"].description}
        jsonLd={faqLd(FAQS)}
      />

      {/* HERO */}
      <section className="relative acx-noise overflow-hidden border-b border-[color:var(--acx-border)] bg-[var(--acx-bg)]">
        {/* Full-bleed hero background — bottle sits on the right, dark space on the left */}
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <img
            src="/hero-bg.png"
            alt=""
            className="h-full w-full object-cover object-[78%_center] lg:object-[right_center]"
          />
          {/* Desktop: left -> right dark gradient so the text column stays readable */}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/10 lg:via-black/70 lg:to-transparent" />
          {/* Mobile: top -> bottom darkening so stacked text never sits on the bright label */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/45 to-black/80 lg:hidden" />
        </div>
        <div className="acx-container relative z-10 grid min-h-[88vh] grid-cols-1 items-start gap-10 px-4 py-16 sm:px-6 lg:min-h-[78vh] lg:grid-cols-2 lg:items-center lg:gap-8 lg:px-10 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.span variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-[color:var(--acx-border)] bg-white/5 px-3 py-1.5 text-[12px] text-white/70">
              <Sparkles size={14} className="text-[#9CA3AF]" /> Professional-grade car care
            </motion.span>
            <motion.h1 variants={fadeUp} className="mt-5 font-display text-[44px] font-extrabold leading-[0.98] tracking-[-0.03em] sm:text-[64px] lg:text-[78px]">
              Intelligent Care.<br />
              <span className="text-white/90">Lasting </span>
              <span className="text-[#E11B22]">Shine.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 max-w-lg text-[17px] leading-relaxed text-white/65">
              Engineered car care products designed to protect, restore and elevate every drive.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/shop"
                data-testid="home-hero-shop-now"
                className="inline-flex items-center gap-2 rounded-[12px] bg-white px-6 py-3.5 text-[15px] font-semibold text-black transition-colors hover:bg-white/90"
              >
                Shop Now <ArrowRight size={17} />
              </Link>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2"><Truck size={18} className="text-white/60" /><span className="text-[13px] text-white/60">Fast Delivery</span></div>
              <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-white/60" /><span className="text-[13px] text-white/60">Secure Payment</span></div>
              <div className="flex items-center gap-2"><Star size={18} className="text-white/60" /><span className="text-[13px] text-white/60">Trusted by 10,000+</span></div>
            </motion.div>
          </motion.div>

          {/* Right column intentionally empty on desktop — the bottle lives in the
              full-bleed background image. Spacer preserves the two-column grid. */}
          <div className="hidden lg:block" aria-hidden="true" data-testid="home-hero-product-visual" />
        </div>
      </section>

      {/* PRODUCT RANGE / ABOUT PREVIEW */}
      <section className="relative border-b border-[color:var(--acx-border)] bg-[var(--acx-bg)]">
        <div className="acx-container grid grid-cols-1 gap-10 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-10 lg:py-28">
          <div className="lg:col-span-3">
            <div className="sticky top-28">
              <span className="hidden text-[12px] uppercase tracking-[0.4em] text-white/40 lg:inline-block lg:[writing-mode:vertical-rl] lg:rotate-180">
                Our Product Range
              </span>
            </div>
          </div>
          <div className="lg:col-span-9">
            <Reveal>
              <h2 className="max-w-3xl font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl lg:text-[56px]">
                Intelligent care for every drive.
              </h2>
              <p className="mt-5 max-w-2xl text-[17px] leading-relaxed text-white/60">
                AutoCareX products are designed to protect, enhance and maintain your vehicle with
                professional-grade care. From everyday cleaning to long-lasting protection, every
                formula works to keep your car looking and performing its best.
              </p>
            </Reveal>
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              variants={stagger}
              className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2"
              data-testid="home-product-range"
            >
              {categories.map((c) => (
                <Reveal key={c.slug}>
                  <Link
                    to={`/shop?category=${c.slug}`}
                    className="group flex items-center justify-between rounded-[14px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-5 transition-colors hover:border-white/20"
                  >
                    <div>
                      <p className="font-display text-[19px] font-semibold text-white">{c.name}</p>
                      <p className="mt-1 text-[13px] text-white/55">{c.tagline}</p>
                    </div>
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white transition-all group-hover:bg-white group-hover:text-black">
                      <ArrowUpRight size={16} />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CATEGORY CARDS */}
      <section className="relative border-b border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
        <div className="acx-container px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[12px] uppercase tracking-[0.22em] text-white/50">Categories</p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl">Explore our range</h2>
            </div>
            <Link to="/shop" className="inline-flex items-center gap-2 text-[14px] text-white/70 transition-colors hover:text-white">
              View all products <ArrowRight size={15} />
            </Link>
          </Reveal>
          <motion.div initial="hidden" whileInView="show" viewport={viewportOnce} variants={stagger} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((c) => (
              <CategoryCard key={c.slug} category={c} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* FEATURED SLIDER */}
      <section className="relative border-b border-[color:var(--acx-border)] bg-[var(--acx-bg)]">
        <div className="acx-container px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-[12px] uppercase tracking-[0.22em] text-white/50">Featured</p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl">
                Designed for every surface, every journey
              </h2>
              <p className="mt-4 text-[16px] text-white/60">
                Precision-crafted solutions that clean, protect and enhance every part of your vehicle.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => scrollSlider(-1)} aria-label="Previous" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[color:var(--acx-border)] bg-white/5 text-white transition-colors hover:bg-white/10"><ChevronLeft size={18} /></button>
              <button onClick={() => scrollSlider(1)} aria-label="Next" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[color:var(--acx-border)] bg-white/5 text-white transition-colors hover:bg-white/10"><ChevronRight size={18} /></button>
            </div>
          </Reveal>

          <div ref={sliderRef} className="no-scrollbar -mx-4 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-2" data-testid="home-featured-products">
            {featured.map((p) => (
              <div key={p.id} className="w-[78%] shrink-0 snap-start sm:w-[44%] lg:w-[27%]">
                <ProductCard product={p} onQuickView={setQuickView} />
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/shop" className="inline-flex items-center gap-2 rounded-[12px] bg-white px-6 py-3.5 text-[15px] font-semibold text-black transition-colors hover:bg-white/90">
              Discover the Goodies <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <div className="border-t border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
        <TrustedMarquee />
      </div>

      <Testimonials items={testimonials} />

      {/* BRAND STATEMENT */}
      <section className="relative acx-noise overflow-hidden bg-[var(--acx-bg)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_circle_at_50%_0%,rgba(156,163,175,0.10),transparent_60%)]" />
        <div className="acx-container relative px-4 py-24 text-center sm:px-6 lg:px-10 lg:py-36">
          <Reveal>
            <h2 className="mx-auto max-w-4xl font-display text-[40px] font-extrabold leading-[1.04] tracking-[-0.03em] sm:text-6xl lg:text-7xl">
              Smart care for modern vehicles.
            </h2>
            <Link
              to="/about"
              className="mt-9 inline-flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-white/5 px-7 py-4 text-[15px] font-semibold text-white transition-colors hover:bg-white/10"
            >
              Learn more about us <ArrowRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative border-t border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
        <div className="acx-container px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <Reveal>
            <p className="text-[12px] uppercase tracking-[0.22em] text-white/50">FAQ</p>
            <h2 className="mt-3 max-w-2xl font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl">
              Frequently asked questions
            </h2>
          </Reveal>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <OurJourney />

      <QuickView product={quickView} open={!!quickView} onOpenChange={(o) => !o && setQuickView(null)} />
    </div>
  );
}
